import { CollapsibleCard } from 'components/DesignSystems/Collapse/CollapsibleCard';
import { useSearchParams } from 'react-router';
import ICSWidget from '../planningView/widgets/ICSWidget';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useActualIncidentId } from '../hooks/commonHooks';
import { useAppSelector, selectGroup } from 'slices/commonSelectors';
import {
  IncidentLogEntry,
  Session,
  SignInInformationRow,
  useGetIncidentLogs,
  WorkLog,
} from 'hooks/incident/useWorkLog';
import { format } from 'date-fns';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useToggle } from 'react-use';
import DailyPersonnelDrawer from './DailyPersonnelDrawer';
import { useDeleteIncidentMemberSession } from 'hooks/incident/useWorkLog';

const createRow = (
  log: IncidentLogEntry,
  groupName: string,
  workLog: WorkLog,
  session: Session
): SignInInformationRow => {
  const signInTime = session.signInTime;
  const dateValue = signInTime || log.updatedDatetime;
  const formattedDate = dateValue
    ? format(new Date(dateValue), 'MMM d yyyy')
    : '';

  return {
    date: formattedDate,
    isNew: false,
    workLogId: workLog.workLogId,
    sessionId: session.sessionId,
    name: log.emailAddress || '',
    organization: groupName || '',
    icsAssignment: log.incidentSeat || '',
    contactInformation: log.emailAddress || '',
    initialCheckinComplete: workLog.initialCheckinComplete || false,
    signInTime: signInTime || '',
    signOutTime: session.signOutTime || '',
    userGuid: log.userGuid,
    sessionVersion: session.version,
  };
};

const mapLogToRows = (
  log: IncidentLogEntry,
  groupName: string
): SignInInformationRow[] => {
  if (log.logs && log.logs.length > 0) {
    return log.logs.flatMap((workLog) => {
      if (workLog.sessions && workLog.sessions.length > 0) {
        return workLog.sessions
          .filter((session) => session.signInTime)
          .map((session) => createRow(log, groupName, workLog, session));
      }
      return [];
    });
  }

  return [];
};

const DailyPersonnelSignInSheet: React.FC = () => {
  const { exportAsForm } = useIcsFormPdfPreview();
  const deleteIncidentSession = useDeleteIncidentMemberSession();
  const incidentId = useActualIncidentId();
  const currentGroup = useAppSelector(selectGroup);
  const [drawerOpen, toggleDrawer] = useToggle(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const { data: incidentLogs } = useGetIncidentLogs();

  const rows = useMemo<SignInInformationRow[]>(() => {
    return (
      incidentLogs?.flatMap((log: IncidentLogEntry) =>
        mapLogToRows(log, currentGroup.group_name)
      ) ?? []
    );
  }, [incidentLogs, currentGroup]);

  const selectedEntity = useMemo(() => {
    return selectedLogId
      ? rows.find((r) => r.sessionId === selectedLogId)
      : undefined;
  }, [selectedLogId, rows]);

  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-personnel-log-sheet`;

  const handleExportICS211A = () => {
    void exportAsForm({
      formname: 'ICS_211A_CG',
      data: {},
      mapSketch: null,
    });
  };
  const columnHelper = createColumnHelper<SignInInformationRow>();
  const columns = useMemo<ColumnDef<SignInInformationRow, any>[]>(
    () => [
      columnHelper.accessor('date', {
        id: 'date',
        header: 'Date',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('signInTime', {
        id: 'signInTime',
        header: 'Time In',
        cell: (info) => {
          const value = info.getValue();
          return value ? new Date(value).toLocaleTimeString() : '-';
        },
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
      }),
      columnHelper.accessor('organization', {
        id: 'organization',
        header: 'Organization',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('icsAssignment', {
        id: 'icsAssignment',
        header: 'ICS Assignment',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('contactInformation', {
        id: 'contactInformation',
        header: 'Contact Information',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('initialCheckinComplete', {
        id: 'initialCheckinComplete',
        header: 'Initial Check-In Status',
        cell: (info) => {
          if (info.getValue()) {
            return <span className="text-success">Complete</span>;
          } else {
            return <span className="text-muted">Incomplete</span>;
          }
        },
      }),
      columnHelper.accessor('signOutTime', {
        id: 'signOutTime',
        header: 'Time Out',
        cell: (info) => {
          const value = info.getValue();
          return value ? new Date(value).toLocaleTimeString() : '-';
        },
      }),
      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <StylishNewButton
              className="button--secondary border-0"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedLogId(row.original.sessionId);
                toggleDrawer(true);
              }}
            >
              <SharedIcon iconName={'edit'} classes="me-1" />
            </StylishNewButton>
            <StylishNewButton
              className="button--secondary border-0"
              onClick={(e) => {
                e.stopPropagation();
                deleteIncidentSession.mutateAsync({
                  sessionId: row.original.sessionId,
                  workLogId: row.original.workLogId,
                  incidentId,
                  userGuid: row.original.userGuid,
                });
              }}
            >
              <SharedIcon iconName="delete" classes="me-1" />
            </StylishNewButton>
          </div>
        ),
        size: 160,
      },
    ],
    [columnHelper, incidentId, toggleDrawer, deleteIncidentSession]
  );

  return (
    <>
      <div className="d-md-flex align-items-center mb-3">
        <h4>Daily Personnel Sign-In Sheet</h4>
      </div>
      <CollapsibleCard
        className="planning-widget"
        contentClassName="content mt-2"
        sectionKey={sectionKey}
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="checklist"
            iconColor="primary"
            title="ICS 211A"
            subtitle="Daily Personnel Sign-In Sheet"
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            icsRoles={['PSC']}
            handleExport={handleExportICS211A}
            exportText="Preview ICS-211A"
          />
        }
      >
        <div className="d-flex justify-content-between align-items-center">
          <span>Personnel Sign-In Information</span>
          <StylishNewButton
            className="mb-2"
            onClick={() => {
              setSelectedLogId(null);
              toggleDrawer(true);
            }}
          >
            <SharedIcon iconName="add" />
            Add Row
          </StylishNewButton>
        </div>
        <PratusTable
          className="layout-auto pratus-table"
          columns={columns}
          data={rows}
        ></PratusTable>
      </CollapsibleCard>
      {drawerOpen && (
        <DailyPersonnelDrawer
          toggle={toggleDrawer}
          selectedEntity={selectedEntity}
        />
      )}
    </>
  );
};

export default DailyPersonnelSignInSheet;
